import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { format } from 'date-fns';
import TableComponent from '../../../components/Table/TableComponent'
import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import { TableCell, TableRow } from '@mui/material';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import { SearchIcon } from '../../../constants/icons';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import ChartButton from '../../../components/Button/ChartButton';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import ClearIcon from "@mui/icons-material/Clear";
import Chart from "../../../components/Chart";
import { BloodSugar, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';

const BloodSugarForm: React.FC<{ patientId: string }> = ({ patientId }) => {

  const [bloodSugarRecords, setBloodSugarRecords] = useState<BloodSugar[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [openDialog, setOpenDialog] = useState(false)
  const [editRecord, setEditRecord] = useState<BloodSugar | null>(null)
  const [showChart, setShowChart] = useState(false)
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });

  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState<BloodSugar | null>(null)
  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {  
  
    fetchBloodSugarRecords(dateRange.startDate, dateRange.endDate);
  }, [page, rowsPerPage, searchTerm]);
  

  const fetchBloodSugarRecords = async (startDate= '', endDate = '') => {
    setLoading(true)
    try {
      const response = await axios.get<ObjectiveResponse>(
        `${BASE_URL}/resource/vitals`,
        {
          params: {
            limit: rowsPerPage,
            page: page + 1,
            from: startDate || '',
            to: endDate || '',
            order_by: 'details->date',
            dir: 2,
            user_id: patientId,
            slug: 'blood-sugar',
            searchkey: searchTerm || undefined,
          },
          headers: getAxiosConfig().headers,
        }
      )

      const { vitals } = response.data.data
      const total = response.data.data.pagination!.total

      if (Array.isArray(vitals) && vitals.length > 0) {
        const formattedData: BloodSugar[] = vitals.map((vital) => ({
          id: vital.id,
          user_id: vital.user_id,
          consult_id: vital.consult_id,
          peripheral_id: vital.peripheral_id,
          slug: vital.slug,
          details: {
            date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
          time: vital.details.time,
          type: vital.details.type,
          value: vital.details.blood_sugar,
          range_code: vital.details.range_code,
          bsFlag: vital.details.bsFlag,
          unit: vital.details.unit, // Ensure unit is included
          blood_sugar: vital.details.blood_sugar, // Ensure blood_sugar is included
          bsFlagColor: vital.details.bsFlagColor,
        },
          freeze: vital.freeze,
          document: vital.document || null,
        }));
        setBloodSugarRecords(formattedData);
        setTotalRecords(total);
      } else {
        setBloodSugarRecords([]); // Reset records if no vitals found
      }
      setTotalRecords(total);
    } catch (error) {
      console.error('Error fetching blood sugar records:', error);
      showSnackbar('Failed to fetch blood sugar records. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };


  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    setPage(0); // Reset to the first page
    fetchBloodSugarRecords(); // Fetch all records again
    setFiltersApplied(false); // Hide the clear button
  };

  const renderRow = (item: BloodSugar, index: number) => (
    <TableRow key={index}>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{page * rowsPerPage + index + 1}</TableCell>
      <TableCell  className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem'}}>{(item.details.date)}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem'}}>{item.details.time}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem'}}>{item.details.type}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem'}}>{item.details.blood_sugar} mg/dL</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem'}}>
      <span
        style={{
          backgroundColor: item.details.range_code,
          padding: '4px 8px',
          borderRadius: '8px',
          color:'#FFFFFF' // Set text color based on background
        }}
      >
        {item.details.bsFlag}
      </span>
      </TableCell>
    </TableRow>
  )

  const chartData = {
    labels: bloodSugarRecords.map((record) => record.details.date),
    datasets: [
      {
        label: 'Blood Sugar (mg/dL)',
        data: bloodSugarRecords.map((record) => record.details.blood_sugar),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 1
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.raw} mg/dL`
        }
      }
    }
  }

  return (
    <div>
      <div className="flex items-end justify-between w-full gap-12">
      <div className="flex gap-2">
          <DateRangePicker
            startDateId={'BloodSugar_Start_Date'}
            endDateId={'BloodSugar_End_Date'}
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates);
              setFiltersApplied(true);
            }}
            value={dateRange} // Pass the selected date range
          />
         
          <IconButton
            icon={<SearchIcon />}
            onClick={() =>{ fetchBloodSugarRecords(dateRange.startDate, dateRange.endDate);
              setFiltersApplied(true); 
            }} // Fetch with current date range and search term
          />
             {filtersApplied && (
                <IconButton
                  icon={<ClearIcon />}
                  onClick={handleClearFilters}
                />
              )}
         
        </div>
        <div className="flex space-x-4">
        
        <ChartButton
          text={showChart ? 'List' : 'Chart'}
          onClick={() => setShowChart((prev) => !prev)}
        />
       </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
       
        </div>
      ) : showChart ? (
        <Chart data={chartData} options={chartOptions} />
      ) : (
        <>
              <TableComponent
                headers={['S.No', 'Date', 'Time', 'Type', 'Value (mg/dL)', 'Result']}
                data={bloodSugarRecords}
                loading={loading}
                renderRow={renderRow} error={''} />
                
                </>
              )}
        <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />

    </div>
  )
}

export default BloodSugarForm;